import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShoppingCart, Bot, Store, Wrench, HelpCircle, CheckCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Simulator } from '../components/Simulator';
import { SupportSimulator } from '../components/SupportSimulator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

function PricingModal({ isOpen, onClose, title, children }) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl bg-slate-950 border-slate-800 text-slate-100 p-0">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle className="text-2xl font-outfit">{title}</DialogTitle>
        </DialogHeader>
        <div className="p-6">{children}</div>
      </DialogContent>
    </Dialog>
  );
}

export default function Pricing() {
  const { language } = useLanguage();
  const [isRecoveryModalOpen, setIsRecoveryModalOpen] = useState(false);
  const [isAssistModalOpen, setIsAssistModalOpen] = useState(false);

  const cards = [
    {
      icon: Store,
      badge: language === 'pt' ? 'Fundação do E-commerce' : 'E-commerce Foundation',
      title: language === 'pt' ? 'Projeto de estruturação inicial' : 'Initial structure project',
      value: language === 'pt' ? 'Setup sob medida' : 'Custom setup',
      description:
        language === 'pt'
          ? 'Ideal para quem está montando a operação e precisa de uma base técnica impecável para começar a faturar.'
          : 'Ideal for those building their operation and needing a flawless technical foundation to start generating revenue.',
      items:
        language === 'pt'
          ? ['Escopo com começo, meio e fim', 'Loja, páginas e fluxos operacionais prontos', 'O passo ideal para quem não quer improvisar']
          : ['Scope with a clear start, middle, and end', 'Store, pages, and operational flows ready', 'The ideal step for those who avoid improvising'],
      color: 'slate',
      action: language === 'pt' ? 'Solicitar proposta' : 'Request proposal',
      href: `/${language}/contact`,
    },
    {
      icon: ShoppingCart,
      badge: 'Cart Recovery',
      title: language === 'pt' ? 'Success Fee' : 'Success Fee',
      value: language === 'pt' ? 'Risco Zero' : 'Zero Risk',
      description:
        language === 'pt'
          ? 'Cobramos apenas um percentual sobre a receita que nossas automações conseguirem resgatar para o seu caixa.'
          : 'We only charge a percentage of the revenue our automations manage to rescue back into your cash flow.',
      items:
        language === 'pt'
          ? ['Sem custo fixo mensal travando seu orçamento', 'Acelerador de lucros imediatos', 'Dashboards transparentes de acompanhamento']
          : ['No fixed monthly costs blocking your budget', 'Immediate profit accelerator', 'Transparent tracking dashboards'],
      color: 'cyan',
      action: language === 'pt' ? 'Simular recuperação' : 'Simulate recovery',
      onClick: () => setIsRecoveryModalOpen(true),
    },
    {
      icon: Bot,
      badge: 'Surge Assist',
      title: language === 'pt' ? 'Por resolução ou escopo' : 'Per resolution or scoped project',
      value: language === 'pt' ? 'Modelo escalável' : 'Scalable model',
      description:
        language === 'pt'
          ? 'Implementamos a IA e o custo se adapta ao volume do seu atendimento. Você paga pela eficiência gerada na operação.'
          : 'We implement AI and the cost adapts to your support volume. You pay for the efficiency generated in the operation.',
      items:
        language === 'pt'
          ? ['Custo previsível alinhado à sua demanda', 'Integração completa com seus canais atuais', 'Transbordo natural para equipe humana']
          : ['Predictable cost aligned with your demand', 'Full integration with your current channels', 'Natural handoff to your human team'],
      color: 'emerald',
      action: language === 'pt' ? 'Calcular economia' : 'Calculate savings',
      onClick: () => setIsAssistModalOpen(true),
    },
    {
      icon: Wrench,
      badge: language === 'pt' ? 'Projetos especiais' : 'Special projects',
      title: language === 'pt' ? 'Engrenagens personalizadas' : 'Custom gears',
      value: language === 'pt' ? 'Foco em ROI' : 'ROI Focused',
      description:
        language === 'pt'
          ? 'Para integrações complexas e gargalos operacionais muito específicos, desenhamos uma proposta focada no seu retorno financeiro.'
          : 'For complex integrations and highly specific operational bottlenecks, we design a proposal focused on your financial return.',
      items:
        language === 'pt'
          ? ['Levantamento técnico profundo da dor', 'Execução dividida em fases seguras', 'Acompanhamento e manutenção contínua']
          : ['Deep technical assessment of the pain point', 'Execution divided into safe phases', 'Ongoing tracking and maintenance'],
      color: 'purple',
      action: language === 'pt' ? 'Trazer meu cenário' : 'Bring my scenario',
      href: `/${language}/contact`,
    },
  ];

  const colorMap = {
    slate: 'border-slate-700/60 text-slate-200 bg-slate-500/10',
    cyan: 'border-cyan-500/20 text-cyan-400 bg-cyan-500/10',
    emerald: 'border-emerald-500/20 text-emerald-400 bg-emerald-500/10',
    purple: 'border-purple-500/20 text-purple-400 bg-purple-500/10',
  };

  return (
    <div className="relative">
      <PricingModal
        isOpen={isRecoveryModalOpen}
        onClose={() => setIsRecoveryModalOpen(false)}
        title={language === 'pt' ? 'Simulador de Recuperação' : 'Recovery Simulator'}
      >
        <Simulator />
        <div className="mt-6 text-center">
          <Link to={`/${language}/contact`} onClick={() => setIsRecoveryModalOpen(false)}>
            <Button className="w-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold">
              {language === 'pt' ? 'Conversar sobre Cart Recovery' : 'Talk about Cart Recovery'}
            </Button>
          </Link>
        </div>
      </PricingModal>

      <PricingModal
        isOpen={isAssistModalOpen}
        onClose={() => setIsAssistModalOpen(false)}
        title={language === 'pt' ? 'Simulador de Economia com IA' : 'AI Savings Simulator'}
      >
        <SupportSimulator />
        <div className="mt-6 text-center">
          <Link to={`/${language}/contact`} onClick={() => setIsAssistModalOpen(false)}>
            <Button className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold">
              {language === 'pt' ? 'Conversar sobre Surge Assist' : 'Talk about Surge Assist'}
            </Button>
          </Link>
        </div>
      </PricingModal>

      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 to-slate-950" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-100 leading-tight tracking-tight mb-6">
            {language === 'pt' ? 'Modelos comerciais que respeitam o seu caixa' : 'Commercial models that respect your cash flow'}
          </h1>
          <p className="text-lg lg:text-xl text-slate-400 leading-relaxed max-w-3xl mx-auto">
            {language === 'pt'
              ? 'Não forçamos pacotes gigantes. Desenhamos formatos de cobrança justos para que você pague apenas pelo que realmente resolve os seus gargalos e gera lucro hoje.'
              : 'We do not force giant packages. We design fair billing formats so you only pay for what actually solves your bottlenecks and generates profit today.'}
          </p>
        </div>
      </section>

      <section className="py-16 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 lg:gap-10">
            {cards.map((card, index) => (
              <div key={index} className="bg-slate-900/50 border border-slate-800 rounded-3xl p-8 lg:p-10 relative overflow-hidden flex flex-col hover:border-slate-700 transition-colors">
                <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4 border ${colorMap[card.color]}`}>
                  <card.icon className="w-4 h-4" />
                  {card.badge}
                </div>
                <h3 className="text-2xl font-bold text-slate-100 mb-2">{card.title}</h3>
                <div className="text-3xl font-black text-slate-100 mb-3">{card.value}</div>
                <p className="text-slate-400 text-sm mb-6 leading-relaxed">{card.description}</p>
                <ul className="space-y-4 mb-8 flex-1">
                  {card.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-slate-300">
                      <CheckCircle className="w-5 h-5 text-cyan-500 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                {card.onClick ? (
                  <Button onClick={card.onClick} className="w-full bg-slate-100 hover:bg-white text-slate-950 font-bold h-12 rounded-xl">
                    {card.action}
                  </Button>
                ) : (
                  <Link to={card.href}>
                    <Button className="w-full bg-slate-100 hover:bg-white text-slate-950 font-bold h-12 rounded-xl">
                      {card.action}
                    </Button>
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="flex items-start gap-4 p-6 bg-slate-900 border border-slate-800 rounded-2xl">
              <Wrench className="w-6 h-6 text-purple-400 flex-shrink-0" />
              <div>
                <h4 className="font-bold text-slate-200 mb-2">
                  {language === 'pt' ? 'O poder do crescimento modular' : 'The power of modular growth'}
                </h4>
                <p className="text-slate-400 text-sm leading-relaxed">
                  {language === 'pt'
                    ? 'A ProcessSurge entra para resolver o seu maior problema atual de forma isolada. Quando essa frente estiver faturando bem e o caixa respirar aliviado, nós expandimos juntos para o próximo gargalo.'
                    : 'ProcessSurge steps in to solve your biggest current problem in isolation. Once that front is billing well and your cash flow is breathing easy, we expand together to the next bottleneck.'}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-6 bg-slate-900 border border-slate-800 rounded-2xl">
              <HelpCircle className="w-6 h-6 text-slate-500 flex-shrink-0" />
              <div>
                <h4 className="font-bold text-slate-200 mb-2">
                  {language === 'pt' ? 'A ferramenta certa sem custos ocultos' : 'The right tool with no hidden costs'}
                </h4>
                <p className="text-slate-400 text-sm leading-relaxed">
                  {language === 'pt'
                    ? 'Se o seu momento exige uma plataforma gigante, nós integramos. Se o seu negócio pede uma solução ágil e enxuta, nós construímos. Nosso papel é garantir que a tecnologia seja um motor e não uma âncora financeira.'
                    : 'If your stage requires a giant platform, we integrate it. If your business needs an agile and lean solution, we build it. Our role is to ensure technology is an engine and not a financial anchor.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
